import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { clientsApi } from '../services/api/clients';

export interface Client {
  id: number;
  name: string;
  email: string;
  created_at: string;
  quotes_count: number;
  recent_quotes?: any[];
}

export interface UseClientsListReturn {
  loading: boolean;
  searchName: string;
  setSearchName: (value: string) => void;
  searchEmail: string;
  setSearchEmail: (value: string) => void;
  registeredAfter: string;
  setRegisteredAfter: (value: string) => void;
  registeredBefore: string;
  setRegisteredBefore: (value: string) => void;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  itemsPerPage: number;
  setItemsPerPage: React.Dispatch<React.SetStateAction<number>>;
  filteredClients: Client[];
  paginatedItems: Client[];
  totalPages: number;
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
  selectedClient: Client | null;
  loadingClientDetails: boolean;
  handleViewDetails: (client: Client) => void;
}

export const useClientsList = (): UseClientsListReturn => {
  const [searchName, setSearchName] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [registeredAfter, setRegisteredAfter] = useState('');
  const [registeredBefore, setRegisteredBefore] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  // ===== Server state via TanStack Query =====

  // Clients list with filter parameters as stable query keys
  const filters = useMemo(() => {
    const f: Record<string, string> = {};
    if (registeredAfter) f.created_after = registeredAfter;
    if (registeredBefore) f.created_before = registeredBefore;
    return f;
  }, [registeredAfter, registeredBefore]);

  const { data, isPending } = useQuery({
    queryKey: ['admin', 'clients', filters],
    queryFn: () => clientsApi.getAdminClients(filters),
  });

  const clients = useMemo(() => data?.clients ?? [], [data]);

  // Client detail query (fetched when modal opens)
  const { data: detailData, isFetching: loadingClientDetails } = useQuery({
    queryKey: ['admin', 'clients', 'detail', selectedClient?.id],
    queryFn: () => clientsApi.getAdminClient(selectedClient!.id),
    enabled: isModalOpen && !!selectedClient,
  });

  // Display the detailed version when loaded, fall back to list version
  const displayClient = useMemo(() => {
    if (detailData?.client) return detailData.client;
    return selectedClient;
  }, [detailData, selectedClient]);

  // Reset to first page when any filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchName, searchEmail, registeredAfter, registeredBefore]);

  const filteredClients = useMemo(() => {
    return clients.filter((client: Client) => {
      const matchesName =
        client.name?.toLowerCase().includes(searchName.toLowerCase()) ?? true;
      const matchesEmail =
        client.email?.toLowerCase().includes(searchEmail.toLowerCase()) ?? true;
      return matchesName && matchesEmail;
    });
  }, [clients, searchName, searchEmail]);

  const totalPages = useMemo(
    () => Math.ceil(filteredClients.length / itemsPerPage),
    [filteredClients.length, itemsPerPage],
  );

  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredClients.slice(startIndex, endIndex);
  }, [filteredClients, currentPage, itemsPerPage]);

  const handleViewDetails = useCallback((client: Client) => {
    setSelectedClient(client);
    setIsModalOpen(true);
  }, []);

  return {
    loading: isPending,
    searchName,
    setSearchName,
    searchEmail,
    setSearchEmail,
    registeredAfter,
    setRegisteredAfter,
    registeredBefore,
    setRegisteredBefore,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    filteredClients,
    paginatedItems,
    totalPages,
    isModalOpen,
    setIsModalOpen,
    selectedClient: displayClient,
    loadingClientDetails,
    handleViewDetails,
  };
};
